import { marbleConfig, railWidth, railY, railZ } from "./config";

export type Vec3 = [number, number, number];
export type Triangles = { vertices: number[]; indices: number[] };

const appendQuad = (
  mesh: Triangles,
  a: Vec3,
  b: Vec3,
  c: Vec3,
  d: Vec3,
) => {
  const index = mesh.vertices.length / 3;
  mesh.vertices.push(...a, ...b, ...c, ...d);
  mesh.indices.push(index, index + 1, index + 2, index, index + 2, index + 3);
};

// One annular surface, with an actual empty center. The same triangles are
// rendered and passed to Rapier, avoiding a second approximate collision mesh.
export const createFunnel = (): Triangles => {
  const mesh: Triangles = { vertices: [], indices: [] };
  const { innerRadius, outerRadius, bottomY, slope, wallHeight, segments, rings } =
    marbleConfig.funnel;
  const point = (r: number, angle: number, y: number): Vec3 => [
    r * Math.cos(angle), y, r * Math.sin(angle),
  ];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const b = ((i + 1) / segments) * Math.PI * 2;
    for (let j = 0; j < rings; j++) {
      const r0 = innerRadius + ((outerRadius - innerRadius) * j) / rings;
      const r1 = innerRadius + ((outerRadius - innerRadius) * (j + 1)) / rings;
      const y0 = bottomY + (r0 - innerRadius) * slope;
      const y1 = bottomY + (r1 - innerRadius) * slope;
      appendQuad(mesh, point(r0, a, y0), point(r1, a, y1), point(r1, b, y1), point(r0, b, y0));
    }
    const rimY = bottomY + (outerRadius - innerRadius) * slope;
    appendQuad(mesh, point(outerRadius, a, rimY), point(outerRadius, a, rimY + wallHeight),
      point(outerRadius, b, rimY + wallHeight), point(outerRadius, b, rimY));
  }
  return mesh;
};

const railPoint = (x: number, lateral: number, yOffset: number): Vec3 => {
  const dx = 0.005;
  const tangentX = 2 * dx;
  const tangentZ = railZ(x + dx) - railZ(x - dx);
  const len = Math.hypot(tangentX, tangentZ);
  const axial = x - (lateral * tangentZ) / len;
  const transverse = railZ(x) + (lateral * tangentX) / len;
  // Send the track toward the camera, using the portrait frame's vertical room.
  return [-transverse, railY(x) + yOffset, axial];
};

export const createRail = (): Triangles => {
  const mesh: Triangles = { vertices: [], indices: [] };
  const { startX, endX, segments, wallHeight, thickness } = marbleConfig.rail;
  for (let i = 0; i < segments; i++) {
    const x0 = startX + ((endX - startX) * i) / segments;
    const x1 = startX + ((endX - startX) * (i + 1)) / segments;
    appendQuad(mesh, railPoint(x0, -railWidth(x0) / 2, 0), railPoint(x1, -railWidth(x1) / 2, 0),
      railPoint(x1, railWidth(x1) / 2, 0), railPoint(x0, railWidth(x0) / 2, 0));
    for (const side of [-1, 1]) {
      appendQuad(mesh, railPoint(x0, side * railWidth(x0) / 2, -thickness),
        railPoint(x1, side * railWidth(x1) / 2, -thickness),
        railPoint(x1, side * railWidth(x1) / 2, wallHeight),
        railPoint(x0, side * railWidth(x0) / 2, wallHeight));
    }
  }
  return mesh;
};

